/**
 * Editor-side icon library. **Ids mirror the backend `src/ai/icon-registry.ts`
 * exactly** — that file feeds the LLM `/ai/suggest-icons`, this one maps each id
 * to a renderable lucide glyph. Keep both in sync. The offline classroom must
 * render the same lucide glyph for a given id (see ELECTRON_BLUEPRINT.md §13).
 */
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  BookOpen,
  BookText,
  Brain,
  Check,
  CircleHelp,
  Layers,
  Lightbulb,
  LineChart,
  Link2,
  ListChecks,
  ListTodo,
  MessageCircle,
  Network,
  Pencil,
  Puzzle,
  Quote,
  RefreshCw,
  Search,
  Sigma,
  Table,
  Timer,
  Trophy,
  Users,
  Volume2,
  X,
  type LucideIcon,
} from 'lucide-react';

export type IconCategory =
  | 'instruction-prompt'
  | 'conceptual'
  | 'content-type'
  | 'assessment'
  | 'interaction';

export interface LibraryIcon {
  id: string;
  name: string;
  category: IconCategory;
  keywords: string[];
  Glyph: LucideIcon;
}

export const ICON_CATEGORIES: { id: IconCategory; label: string }[] = [
  { id: 'instruction-prompt', label: 'Instruction & Prompts' },
  { id: 'conceptual', label: 'Conceptual' },
  { id: 'content-type', label: 'Content Type' },
  { id: 'assessment', label: 'Assessment' },
  { id: 'interaction', label: 'Interaction' },
];

export const ICON_LIBRARY: LibraryIcon[] = [
  // Instruction & Prompts
  { id: 'question', name: 'Question', category: 'instruction-prompt', keywords: ['ask', 'think', 'doubt', 'query', 'why'], Glyph: CircleHelp },
  { id: 'important', name: 'Important', category: 'instruction-prompt', keywords: ['warning', 'note', 'remember', 'careful', 'caution'], Glyph: AlertTriangle },
  { id: 'idea', name: 'Idea', category: 'instruction-prompt', keywords: ['lightbulb', 'tip', 'insight', 'eureka', 'hint'], Glyph: Lightbulb },
  { id: 'next-step', name: 'Next step', category: 'instruction-prompt', keywords: ['arrow', 'flow', 'continue', 'forward', 'proceed'], Glyph: ArrowRight },
  { id: 'participation', name: 'Participation', category: 'instruction-prompt', keywords: ['hand', 'raise', 'volunteer', 'answer'], Glyph: Users },
  { id: 'correct', name: 'Correct', category: 'instruction-prompt', keywords: ['tick', 'check', 'right', 'yes', 'true'], Glyph: Check },
  { id: 'incorrect', name: 'Incorrect', category: 'instruction-prompt', keywords: ['cross', 'wrong', 'no', 'false', 'mistake'], Glyph: X },
  // Conceptual
  { id: 'thinking', name: 'Thinking', category: 'conceptual', keywords: ['brain', 'concept', 'reason', 'mind'], Glyph: Brain },
  { id: 'problem-solving', name: 'Problem solving', category: 'conceptual', keywords: ['puzzle', 'solve', 'challenge'], Glyph: Puzzle },
  { id: 'step-by-step', name: 'Step-by-step', category: 'conceptual', keywords: ['layers', 'sequence', 'stages', 'process'], Glyph: Layers },
  { id: 'analyze', name: 'Analyze', category: 'conceptual', keywords: ['magnifying', 'inspect', 'examine', 'observe'], Glyph: Search },
  { id: 'connection', name: 'Connection', category: 'conceptual', keywords: ['link', 'relation', 'between'], Glyph: Link2 },
  // Content Type
  { id: 'definition', name: 'Definition', category: 'content-type', keywords: ['glossary', 'meaning', 'term'], Glyph: BookText },
  { id: 'example', name: 'Example', category: 'content-type', keywords: ['sample', 'instance', 'illustration'], Glyph: Quote },
  { id: 'formula', name: 'Formula', category: 'content-type', keywords: ['math', 'equation', 'expression'], Glyph: Sigma },
  { id: 'diagram', name: 'Diagram', category: 'content-type', keywords: ['network', 'schema', 'graph'], Glyph: Network },
  { id: 'graph', name: 'Graph', category: 'content-type', keywords: ['chart', 'plot', 'line', 'data'], Glyph: LineChart },
  { id: 'bar-chart', name: 'Bar chart', category: 'content-type', keywords: ['chart', 'compare', 'statistics'], Glyph: BarChart3 },
  { id: 'table', name: 'Table', category: 'content-type', keywords: ['grid', 'rows', 'columns'], Glyph: Table },
  { id: 'story', name: 'Story', category: 'content-type', keywords: ['book', 'narrative', 'context'], Glyph: BookOpen },
  { id: 'reading', name: 'Reading', category: 'content-type', keywords: ['book', 'text', 'passage', 'study'], Glyph: BookOpen },
  // Assessment
  { id: 'mcq', name: 'Question (MCQ)', category: 'assessment', keywords: ['options', 'choose', 'select'], Glyph: ListChecks },
  { id: 'checklist', name: 'Checklist', category: 'assessment', keywords: ['todo', 'list', 'goals', 'objectives'], Glyph: ListTodo },
  { id: 'timer', name: 'Timer', category: 'assessment', keywords: ['clock', 'countdown', 'timed'], Glyph: Timer },
  { id: 'achievement', name: 'Achievement', category: 'assessment', keywords: ['trophy', 'reward', 'success'], Glyph: Trophy },
  { id: 'retry', name: 'Retry', category: 'assessment', keywords: ['refresh', 'redo', 'again', 'reset'], Glyph: RefreshCw },
  // Interaction
  { id: 'discussion', name: 'Discussion', category: 'interaction', keywords: ['chat', 'talk', 'conversation'], Glyph: MessageCircle },
  { id: 'listen', name: 'Listen', category: 'interaction', keywords: ['speaker', 'audio', 'narration', 'sound'], Glyph: Volume2 },
  { id: 'write', name: 'Write', category: 'interaction', keywords: ['pencil', 'notes', 'task', 'practice'], Glyph: Pencil },
  { id: 'group-task', name: 'Group task', category: 'interaction', keywords: ['users', 'team', 'pair', 'collab'], Glyph: Users },
];

const BY_ID = new Map(ICON_LIBRARY.map((i) => [i.id, i]));

/** Resolve an icon id to its library entry (falls back to "idea"). */
export function findLibraryIcon(id: string | undefined): LibraryIcon {
  return (id && BY_ID.get(id)) || BY_ID.get('idea')!;
}

/** Keyword/category search for the icon picker. */
export function searchIcons(query: string, category?: IconCategory): LibraryIcon[] {
  const q = query.trim().toLowerCase();
  return ICON_LIBRARY.filter((i) => {
    if (category && i.category !== category) return false;
    if (!q) return true;
    if (i.id.includes(q) || i.name.toLowerCase().includes(q)) return true;
    return i.keywords.some((k) => k.includes(q));
  });
}
